import { create } from 'zustand';
import type {
  ProductContentChangeSetSummary,
  ProductContentReviewChange,
  StageCollectionContentResponse,
  StageProductDescriptionResponse,
  StageProductSeoResponse,
  StageProductTeaserResponse,
  StageProductTypeResponse,
} from '@shared/types/product-content';
import {
  commitProductContentChanges,
  discardProductContentChange,
  discardProductContentDraft,
  getProductContentDraft,
  getProductContentDraftSummary,
  stageCollectionContent,
  stageProductDescription,
  stageProductSeo,
  stageProductTeaser,
  stageProductType,
} from '@/lib/api';

export type ProductContentStageResult =
  | StageProductSeoResponse
  | StageProductTeaserResponse
  | StageProductTypeResponse
  | StageProductDescriptionResponse
  | StageCollectionContentResponse;
import { useAuthStore } from './authStore';

interface ProductContentState {
  changeSet: ProductContentChangeSetSummary | null;
  changes: ProductContentReviewChange[];
  activeChangeCount: number;
  revision: number;
  summaryLoading: boolean;
  reviewLoading: boolean;
  reviewLoaded: boolean;
  /**
   * The review's load error (fetchReview). The background summary never sets it, so a blip in
   * the top bar's count can't show on the queue page as a failed review.
   */
  error: string | null;
  fetchSummary: (options?: { force?: boolean }) => Promise<void>;
  fetchReview: (options?: { force?: boolean }) => Promise<void>;
  applyActiveCount: (count: number, changeSet?: ProductContentChangeSetSummary | null) => void;
  applyStageResult: (result: ProductContentStageResult) => void;
  invalidateReview: () => void;
  clearError: () => void;
  reset: () => void;
}

function authReady(): boolean {
  const user = useAuthStore.getState().user;
  return Boolean(user?.token && user.active_team?.id);
}

/** Skip background summary refetches when the last success was this recent. */
export const PRODUCT_CONTENT_SUMMARY_TTL_MS = 20_000;

let summaryRequestSequence = 0;
let reviewRequestSequence = 0;
let summaryFetchedAt = 0;

export const useProductContentStore = create<ProductContentState>()((set, get) => ({
  changeSet: null,
  changes: [],
  activeChangeCount: 0,
  revision: 0,
  summaryLoading: false,
  reviewLoading: false,
  reviewLoaded: false,
  error: null,

  clearError: () => set({ error: null }),

  reset: () => {
    summaryFetchedAt = 0;
    set((state) => ({
      changeSet: null,
      changes: [],
      activeChangeCount: 0,
      revision: state.revision + 1,
      summaryLoading: false,
      reviewLoading: false,
      reviewLoaded: false,
      error: null,
    }));
  },

  applyActiveCount: (count, changeSet) =>
    set((state) => ({
      activeChangeCount: count,
      changeSet: changeSet === undefined ? state.changeSet : changeSet,
      revision: state.revision + 1,
      summaryLoading: false,
      reviewLoading: false,
      reviewLoaded: false,
    })),

  applyStageResult: (result) =>
    set((state) => {
      let changes = state.changes;
      const canonicalChange = result.change ?? null;
      if (result.action === 'reverted' && result.change_id) {
        changes = changes.filter((change) => change.id !== result.change_id);
      } else if (canonicalChange) {
        const existingIndex = changes.findIndex(
          (change) => change.id === canonicalChange.id
        );
        changes =
          existingIndex === -1
            ? [...changes, canonicalChange].sort(
                (a, b) => a.position - b.position
              )
            : changes.map((change, index) =>
                index === existingIndex ? canonicalChange : change
              );
      }

      const mutationWasReconciled =
        result.action === 'reverted' ||
        result.action === 'noop' ||
        canonicalChange !== null;
      const reviewIsComplete =
        result.active_change_count === 0 ||
        (state.reviewLoaded && mutationWasReconciled) ||
        (result.active_change_count === 1 &&
          canonicalChange !== null &&
          changes.length === 1);

      return {
        changeSet: result.change_set,
        changes,
        activeChangeCount: result.active_change_count,
        revision: state.revision + 1,
        summaryLoading: false,
        reviewLoading: false,
        reviewLoaded: reviewIsComplete,
        error: null,
      };
    }),

  invalidateReview: () => set({ reviewLoaded: false }),

  fetchSummary: async (options = {}) => {
    if (!authReady()) return;
    if (
      !options.force &&
      summaryFetchedAt > 0 &&
      Date.now() - summaryFetchedAt < PRODUCT_CONTENT_SUMMARY_TTL_MS
    ) {
      return;
    }
    const requestId = ++summaryRequestSequence;
    const revision = get().revision;
    set({ summaryLoading: true });
    try {
      const res = await getProductContentDraftSummary();
      if (
        requestId !== summaryRequestSequence ||
        revision !== get().revision
      ) {
        return;
      }
      if (res.error) {
        console.warn('Product content summary failed:', res.error);
        set({ summaryLoading: false });
        return;
      }
      const changeSet = res.data?.change_set ?? null;
      summaryFetchedAt = Date.now();
      set({
        changeSet,
        activeChangeCount: changeSet?.active_change_count ?? 0,
        summaryLoading: false,
      });
    } catch (e) {
      if (
        requestId !== summaryRequestSequence ||
        revision !== get().revision
      ) {
        return;
      }
      console.warn('Product content summary failed:', e);
      set({ summaryLoading: false });
    }
  },

  fetchReview: async (options = {}) => {
    if (!authReady()) return;
    if (get().reviewLoaded && !options.force) return;
    const requestId = ++reviewRequestSequence;
    const revision = get().revision;
    set({ reviewLoading: true, error: null });
    try {
      const res = await getProductContentDraft();
      if (requestId !== reviewRequestSequence || revision !== get().revision) {
        return;
      }
      if (res.error) {
        set({ error: res.error, reviewLoading: false });
        return;
      }
      const changeSet = res.data?.change_set ?? null;
      const changes = res.data?.changes ?? [];
      set({
        changeSet,
        changes,
        activeChangeCount: changeSet?.active_change_count ?? changes.length,
        reviewLoading: false,
        reviewLoaded: true,
      });
    } catch (e) {
      if (requestId !== reviewRequestSequence || revision !== get().revision) {
        return;
      }
      set({
        error: e instanceof Error ? e.message : 'Failed to load product content review',
        reviewLoading: false,
      });
    }
  },
}));

export async function runStageProductSeo(input: {
  product_id: string;
  seo_title: string;
  seo_description: string;
}) {
  const res = await stageProductSeo(input);
  if (res.error) throw new Error(res.error);
  const data = res.data!;
  useProductContentStore.getState().applyStageResult(data);
  return data;
}

export async function runStageProductTeaser(input: {
  product_id: string;
  teaser: string;
}) {
  const res = await stageProductTeaser(input);
  if (res.error) throw new Error(res.error);
  const data = res.data!;
  useProductContentStore.getState().applyStageResult(data);
  return data;
}

export async function runStageProductDescription(input: {
  product_id: string;
  description_html: string;
  features: string;
  specifications: string;
}) {
  const res = await stageProductDescription(input);
  if (res.error) throw new Error(res.error);
  const data = res.data!;
  useProductContentStore.getState().applyStageResult(data);
  return data;
}

export async function runStageProductType(input: {
  product_id: string;
  product_type_id?: string | null;
  product_type?: string;
}) {
  const res = await stageProductType(input);
  if (res.error) throw new Error(res.error);
  const data = res.data!;
  useProductContentStore.getState().applyStageResult(data);
  return data;
}

export async function runStageCollectionContent(input: {
  collection_id: string;
  seo_title: string;
  seo_description: string;
  description_html: string;
}) {
  const res = await stageCollectionContent(input);
  if (res.error) throw new Error(res.error);
  const data = res.data!;
  useProductContentStore.getState().applyStageResult(data);
  return data;
}

export async function runDiscardProductContentChange(changeId: string) {
  const res = await discardProductContentChange(changeId);
  if (res.error) throw new Error(res.error);
  const data = res.data!;
  useProductContentStore.getState().applyActiveCount(data.active_change_count, data.change_set);
  return data;
}

export async function runDiscardProductContentDraft() {
  const res = await discardProductContentDraft();
  if (res.error) throw new Error(res.error);
  const data = res.data!;
  useProductContentStore.setState((state) => ({
    changeSet: data.change_set,
    changes: [],
    activeChangeCount: 0,
    revision: state.revision + 1,
    summaryLoading: false,
    reviewLoading: false,
    reviewLoaded: true,
  }));
  return data;
}

export async function runCommitProductContent(changeIds?: string[]) {
  const res = await commitProductContentChanges(changeIds);
  if (res.error) throw new Error(res.error);
  return res.data!;
}
